import { format, isToday } from 'date-fns'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useSalesLead } from '@/models/salesLead'
import { FollowUpReminderDialog } from '../SalesLeads/FollowUpReminderDialog'

function dueLabel(date: Date, overdue: boolean): { text: string; className: string } {
  if (overdue) {
    const days = Math.max(1, Math.round((Date.now() - date.getTime()) / 86_400_000))
    return { text: `${format(date, 'MMM d')} · ${days}d overdue`, className: 'text-red-600 font-medium' }
  }
  if (isToday(date)) return { text: 'Today', className: 'text-amber-600 font-medium' }
  return { text: format(date, 'MMM d'), className: 'text-muted-foreground' }
}

interface NextActionDueCellProps {
  leadId: string
  dueDate: string | null
  overdue: boolean
  readOnly?: boolean
}

/** The deal's due chip; clicking it opens the lead's follow-up reminder dialog. */
export function NextActionDueCell({ leadId, dueDate, overdue, readOnly = false }: NextActionDueCellProps) {
  const [open, setOpen] = useState(false)
  // Only fetch the lead the dialog needs once it is actually opened.
  const { data: lead } = useSalesLead(open ? leadId : '')
  const label = dueDate ? dueLabel(new Date(dueDate), overdue) : null

  if (readOnly) {
    return (
      <span className={cn('px-1.5 text-xs', label?.className ?? 'text-muted-foreground')}>
        {label?.text ?? '—'}
      </span>
    )
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className={cn('h-auto px-1.5 py-0.5 text-xs', label?.className ?? 'text-muted-foreground')}
        onClick={() => setOpen(true)}
      >
        {label?.text ?? 'Set date'}
      </Button>
      {lead && <FollowUpReminderDialog lead={lead} open={open} onOpenChange={setOpen} />}
    </>
  )
}
